import { format } from "date-fns";
import { ArrowLeft, RefreshCw, Users, Edit2 } from "lucide-react";
import { CampaignCallLogs } from "./CampaignCallLogs";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { campaignService } from "../../services/campaignService";
import { Campaign, CampaignStatus } from "../../types/campaign";
import { FileUploader } from "../leads/FileUploader";
import { LeadUploadsList } from "../leads/LeadUploadsList";
import FormPreview from "../schema-builder/FormPreview";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { useToast } from "../ui/use-toast";
import { CampaignLeadsList } from "./CampaignLeadsList";
import { CampaignSettingsForm } from "./CampaignSettingsForm";
import CampaignSpecificCallLogsList from "./CampaignSpecificCallLogsList";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

export function CampaignDetail() {
  const { id, templateName } = useParams<{
    id: string;
    templateName: string;
  }>();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [campaignLeads, setCampaignLeads] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [isEditingOverview, setIsEditingOverview] = useState(false);
  const [editedCampaign, setEditedCampaign] = useState<Partial<Campaign>>({});
  const { toast } = useToast();
  const navigate = useNavigate();

  const fetchCampaign = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const { data, error } = await campaignService.getCampaign(id);
      if (error) throw error;
      if (data) {
        setCampaign(data);
      }
    } catch (error) {
      console.error("Error fetching campaign:", error);
      toast({
        title: "Error",
        description: "Failed to load campaign details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaignLeads = async () => {
    if (!id) return;

    try {
      const { data, error } = await campaignService.getCampaignLeads(id);
      if (error) throw error;
      if (data) {
        setCampaignLeads(data);
      }
    } catch (error) {
      console.error("Error fetching campaign leads:", error);
      toast({
        title: "Error",
        description: "Failed to load campaign leads",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchCampaign();
    fetchCampaignLeads();
  }, [id]);

  const handleRefresh = () => {
    fetchCampaign();
    fetchCampaignLeads();
  };

  const handleBack = () => {
    if (templateName) {
      navigate(`/u/${templateName}`);
    } else {
      navigate("/campaigns");
    }
  };

  const handleLeadsAdded = () => {
    fetchCampaignLeads();
    toast({
      title: "Leads added",
      description: "Leads have been added to the campaign",
    });
  };

  const handleUploadComplete = () => {
    fetchCampaignLeads();
    toast({
      title: "Upload complete",
      description: "Files have been uploaded and processed",
    });
  };

  const handleSaveConfiguration = async (values: Record<string, any>) => {
    if (!id) return;

    try {
      const { error } = await campaignService.updateCampaign(id, {
        configuration_data: values,
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Campaign configuration saved successfully",
      });

      fetchCampaign();
    } catch (error) {
      console.error("Error saving campaign configuration:", error);
      toast({
        title: "Error",
        description: "Failed to save campaign configuration",
        variant: "destructive",
      });
    }
  };

  const handleEditOverview = (campaign: Campaign) => {
    setEditedCampaign({
      name: campaign.name,
      description: campaign.description,
      status: campaign.status,
      call_start_time: campaign.call_start_time,
      call_end_time: campaign.call_end_time,
    });
    setIsEditingOverview(true);
  };

  const handleSaveOverview = async () => {
    if (!id || !campaign) return;

    try {
      const { error } = await campaignService.updateCampaign(id, editedCampaign);
      
      if (error) throw error;

      toast({
        title: "Success",
        description: "Campaign details updated successfully",
      });

      await fetchCampaign();
      setIsEditingOverview(false);
    } catch (error) {
      console.error("Error updating campaign:", error);
      toast({
        title: "Error",
        description: "Failed to update campaign details",
        variant: "destructive",
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditingOverview(false);
    setEditedCampaign({});
  };

  const getStatusBadge = (status: CampaignStatus) => {
    switch (status) {
      case "draft":
        return <Badge variant="outline">Draft</Badge>;
      case "active":
        return <Badge variant="success">Active</Badge>;
      case "paused":
        return <Badge variant="warning">Paused</Badge>;
      case "completed":
        return <Badge variant="secondary">Completed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return <div className="py-8 text-center">Loading...</div>;
  }

  if (!campaign) {
    return <div className="py-8 text-center">Campaign not found</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">{campaign.name}</h1>
          {getStatusBadge(campaign.status as CampaignStatus)}
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="uploads">Uploads</TabsTrigger>
          <TabsTrigger value="leads">Leads</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="call_logs">Call Logs</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Campaign Details</CardTitle>
                  <CardDescription>
                    View and manage campaign information
                  </CardDescription>
                </div>
                {!isEditingOverview && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditOverview(campaign)}
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                )}
                {isEditingOverview && (
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      onClick={handleSaveOverview}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!isEditingOverview ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      Description
                    </h3>
                    <p className="mt-1">
                      {campaign.description || "No description"}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Status</h3>
                    <p className="mt-1">
                      {getStatusBadge(campaign.status as CampaignStatus)}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      Call Window
                    </h3>
                    <p className="mt-1">
                      {campaign.call_start_time && campaign.call_end_time ? (
                        <span>
                          {format(
                            new Date(`2000-01-01T${campaign.call_start_time}`),
                            "p",
                          )}{" "}
                          -{" "}
                          {format(
                            new Date(`2000-01-01T${campaign.call_end_time}`),
                            "p",
                          )}
                        </span>
                      ) : (
                        "No call window specified"
                      )}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      Lead Count
                    </h3>
                    <p className="mt-1 flex items-center">
                      <Users className="h-4 w-4 mr-2 text-gray-400" />
                      {campaignLeads.length}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Created</h3>
                    <p className="mt-1">
                      {format(new Date(campaign.created_at), "PPpp")}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="edit-name">Name</Label>
                    <Input
                      id="edit-name"
                      value={editedCampaign.name || ""}
                      onChange={(e) =>
                        setEditedCampaign({ ...editedCampaign, name: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="edit-description">Description</Label>
                    <Textarea
                      id="edit-description"
                      value={editedCampaign.description || ""}
                      onChange={(e) =>
                        setEditedCampaign({
                          ...editedCampaign,
                          description: e.target.value,
                        })
                      }
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-status">Status</Label>
                    <Select
                      value={editedCampaign.status || "draft"}
                      onValueChange={(value) =>
                        setEditedCampaign({
                          ...editedCampaign,
                          status: value as CampaignStatus,
                        })
                      }
                    >
                      <SelectTrigger id="edit-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="paused">Paused</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Call Window</Label>
                    <div className="flex items-center space-x-2">
                      <Input
                        type="time"
                        value={editedCampaign.call_start_time || "09:00"}
                        onChange={(e) =>
                          setEditedCampaign({
                            ...editedCampaign,
                            call_start_time: e.target.value,
                          })
                        }
                      />
                      <span>to</span>
                      <Input
                        type="time"
                        value={editedCampaign.call_end_time || "17:00"}
                        onChange={(e) =>
                          setEditedCampaign({
                            ...editedCampaign,
                            call_end_time: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                  {/* Read-only fields */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      Lead Count
                    </h3>
                    <p className="mt-1 flex items-center">
                      <Users className="h-4 w-4 mr-2 text-gray-400" />
                      {campaignLeads.length}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Created</h3>
                    <p className="mt-1">
                      {format(new Date(campaign.created_at), "PPpp")}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Campaign Statistics</CardTitle>
              <CardDescription>Key metrics for this campaign</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-black p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-500">
                    Total Leads
                  </h3>
                  <p className="mt-1 text-2xl font-semibold">
                    {campaignLeads.length}
                  </p>
                </div>
                {/* Add more statistics as needed */}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="details">
          {campaign.feature_templates?.schema_template?.schema?.properties && (
            <FormPreview
              schema={
                campaign.feature_templates.schema_template.schema.properties
              }
              initialData={campaign.configuration_data}
              onSave={handleSaveConfiguration}
              title="Campaign Configuration Form"
              description="Configure the settings for this campaign using the form below"
            />
          )}
        </TabsContent>

        <TabsContent value="uploads" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Upload Files</CardTitle>
              <CardDescription>
                Upload files to automatically create leads and add them to this
                campaign
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FileUploader
                organizationId={campaign.organization_id}
                onUploadComplete={handleUploadComplete}
                campaignId={id}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Uploaded Files</CardTitle>
              <CardDescription>
                View all files uploaded for this campaign
              </CardDescription>
            </CardHeader>
            <CardContent>
              <LeadUploadsList
                organizationId={campaign.organization_id}
                campaignId={id}
                onRefresh={handleRefresh}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leads">
          <Card>
            <CardHeader>
              <CardTitle>Campaign Leads</CardTitle>
              <CardDescription>Manage leads in this campaign</CardDescription>
            </CardHeader>
            <CardContent>
              <CampaignLeadsList
                campaignId={id || ""}
                organizationId={campaign.organization_id}
                onRefresh={handleRefresh}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activity">
          <Card>
            <CardHeader>
              <CardTitle>Activity</CardTitle>
              <CardDescription>
                View call activity grouped by lead with outcomes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CampaignCallLogs
                campaignId={id || ""}
                organizationId={campaign.organization_id}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="call_logs">
          <Card>
            <CardHeader>
              <CardTitle>Call Logs</CardTitle>
              <CardDescription>
                View detailed history of all calls in this campaign
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CampaignSpecificCallLogsList
                campaignId={id || ""}
                organizationId={campaign.organization_id}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Campaign Settings</CardTitle>
                <CardDescription>
                  Configure advanced campaign settings
                </CardDescription>
              </CardHeader>
              <CardContent>
                <CampaignSettingsForm
                  campaign={campaign}
                  onComplete={handleRefresh}
                />
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
